import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Globe, Play, Plus, Trash2, Import, Upload } from 'lucide-react';
import { notify } from '@/components/core';

export const ApiRequestPanel = ({ data, onChange }: any) => {
    // Helper to update specific field
    const handleChange = (field: string, value: any) => {
        onChange({ ...data, [field]: value });
    };

    const handleHeaderChange = (key: string, value: string, index: number) => {
        // We store headers as JSON string or Object?
        // Node Data stores stringified JSON usually for consistency or checking.
        // Let's store as Object in 'headers' property internally, or parse it.
        // For existing pattern: check how data is passed. 'data' is the node.data object.

        // Let's assume data.headers is a JSON string or object.
        let headers = {};
        try {
            headers = typeof data.headers === 'string' ? JSON.parse(data.headers) : (data.headers || {});
        } catch { headers = {}; }

        const entries = Object.entries(headers);
        if (index >= entries.length) {
            // New
            headers = { ...headers, [key]: value };
        } else {
            // Edit
            const newHeaders: any = {};
            entries.forEach((entry, i) => {
                if (i === index) newHeaders[key] = value;
                else newHeaders[entry[0]] = entry[1];
            });
            headers = newHeaders;
        }

        handleChange('headers', JSON.stringify(headers));
    };

    const removeHeader = (index: number) => {
        let headers = {};
        try {
            headers = typeof data.headers === 'string' ? JSON.parse(data.headers) : (data.headers || {});
        } catch { return; }

        const entries = Object.entries(headers);
        const newHeaders: any = {};
        entries.forEach((entry, i) => {
            if (i !== index) newHeaders[entry[0]] = entry[1];
        });

        handleChange('headers', JSON.stringify(newHeaders));
    };

    // Parse headers to array for UI
    const getHeadersEnv = () => {
        try {
            const h = typeof data.headers === 'string' ? JSON.parse(data.headers) : (data.headers || {});
            return Object.entries(h);
        } catch { return []; }
    };

    return (
        <div className="space-y-4 p-1">
            <div className="flex items-center gap-2 mb-4 p-2 bg-slate-50 rounded border">
                <Globe className="w-5 h-5 text-blue-500" />
                <div className="flex-1">
                    <h4 className="font-semibold text-sm">Universal API Node</h4>
                    <p className="text-xs text-muted-foreground">Connect to any REST API</p>
                </div>
                <SwaggerImportDialog onImport={({ method, url, body }) => {
                    if (method) handleChange('method', method);
                    if (url) handleChange('url', url);
                    if (body) handleChange('body', body);
                    notify.success("Imported Endpoint Config");
                }} />
            </div>

            <div className="flex gap-2">
                <Select value={data.method || 'GET'} onValueChange={(v) => handleChange('method', v)}>
                    <SelectTrigger className="w-[100px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="DELETE">DELETE</SelectItem>
                        <SelectItem value="PATCH">PATCH</SelectItem>
                    </SelectContent>
                </Select>
                <Input
                    placeholder="https://api.example.com/v1/..."
                    value={data.url || ''}
                    onChange={(e) => handleChange('url', e.target.value)}
                    className="flex-1 font-mono text-sm"
                />
            </div>

            <Tabs defaultValue="params" className="w-full">
                <TabsList className="w-full grid grid-cols-4">
                    <TabsTrigger value="params">Params</TabsTrigger>
                    <TabsTrigger value="auth">Auth</TabsTrigger>
                    <TabsTrigger value="headers">Heads</TabsTrigger>
                    <TabsTrigger value="body">Body</TabsTrigger>
                </TabsList>

                <TabsContent value="params" className="pt-4 space-y-2">
                    <p className="text-xs text-muted-foreground mb-2">Query Parameters (appended to URL)</p>
                    {/* TODO: Key-Value Editor for Params */}
                    <div className="text-sm text-slate-500 italic">params coming soon... use URL for now.</div>
                </TabsContent>

                <TabsContent value="auth" className="pt-4 space-y-4">
                    <div className="space-y-2">
                        <Label>Authentication Type</Label>
                        <Select value={data.authType || 'None'} onValueChange={(v) => handleChange('authType', v)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="None">No Auth</SelectItem>
                                <SelectItem value="Bearer">Bearer Token</SelectItem>
                                <SelectItem value="Basic">Basic Auth</SelectItem>
                                <SelectItem value="ApiKey">API Key (Header)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {data.authType && data.authType !== 'None' && (
                        <div className="space-y-2">
                            <Label>Token / Key</Label>
                            <Input
                                type="password"
                                value={data.authToken || ''}
                                onChange={(e) => handleChange('authToken', e.target.value)}
                                placeholder={data.authType === 'Basic' ? 'user:pass (base64)' : 'sk_...'}
                            />
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="headers" className="pt-4 space-y-2">
                    <div className="flex justify-between items-center">
                        <Label>Custom Headers</Label>
                        <Button variant="ghost" size="sm" onClick={() => handleHeaderChange('New-Header', 'Value', 999)}>
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                    {getHeadersEnv().map(([key, val], idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                            <Input value={key} onChange={(e) => handleHeaderChange(e.target.value, val as string, idx)} className="h-8 font-mono text-xs" />
                            <Input value={val as string} onChange={(e) => handleHeaderChange(key, e.target.value, idx)} className="h-8 font-mono text-xs flex-1" />
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeHeader(idx)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                    ))}
                    {getHeadersEnv().length === 0 && <div className="text-xs text-muted-foreground">No custom headers.</div>}
                </TabsContent>

                <TabsContent value="body" className="pt-4 space-y-2">
                    <Label>Request Body (JSON)</Label>
                    <Textarea
                        className="font-mono text-xs min-h-[200px]"
                        value={data.body || ''}
                        onChange={(e) => handleChange('body', e.target.value)}
                        placeholder='{ "key": "value" }'
                    />
                    <div className="flex justify-end">
                        <Button size="sm" variant="outline" onClick={() => {
                            try {
                                const fmt = JSON.stringify(JSON.parse(data.body), null, 2);
                                handleChange('body', fmt);
                            } catch { notify.error("Invalid JSON"); }
                        }}>Format JSON</Button>
                    </div>
                </TabsContent>
            </Tabs>

            <div className="pt-4 border-t mt-4">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={() => notify.success("Request Test Started (Mock)...")}>
                    <Play className="w-4 h-4 mr-2" />
                    Test Request
                </Button>
            </div>
        </div>
    );
};

const SwaggerImportDialog = ({ onImport }: { onImport: (data: any) => void }) => {
    const [json, setJson] = useState('');
    const [candidates, setCandidates] = useState<any[]>([]);
    const [open, setOpen] = useState(false);

    const parseSwagger = () => {
        try {
            const doc = JSON.parse(json);
            const found: any[] = [];

            // Handle Swagger 2.0 and OpenAPI 3.0
            const paths = doc.paths || {};
            const baseUrl = doc.host ? (doc.schemes?.[0] || 'https') + '://' + doc.host + (doc.basePath || '') : '';

            Object.keys(paths).forEach(path => {
                Object.keys(paths[path]).forEach(method => {
                    const op = paths[path][method];
                    if (['get', 'post', 'put', 'delete', 'patch'].includes(method.toLowerCase())) {
                        found.push({
                            method: method.toUpperCase(),
                            path: path,
                            fullUrl: baseUrl + path,
                            summary: op.summary || op.operationId || path,
                            // Basic body extraction logic (Schema > Properties)
                            body: op.parameters?.find((p: any) => p.in === 'body')?.schema ||
                                op.requestBody?.content?.['application/json']?.schema || null
                        });
                    }
                });
            });
            setCandidates(found);
            if (found.length === 0) notify.warning("No endpoints found in JSON.");
        } catch (e) {
            notify.error("Invalid JSON content");
        }
    };

    const generateBodySample = (schema: any): string => {
        if (!schema) return '';
        if (schema.example) return JSON.stringify(schema.example, null, 2);

        // Walk properties
        if (schema.properties) {
            const obj: any = {};
            Object.keys(schema.properties).forEach(key => {
                const prop = schema.properties[key];
                obj[key] = prop.type === 'integer' ? 0 : prop.type === 'boolean' ? false : "string";
            });
            return JSON.stringify(obj, null, 2);
        }
        return '{}';
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                    <Import className="w-3 h-3" /> Import
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Import from OpenAPI / Swagger</DialogTitle>
                    <DialogDescription>Paste your swagger.json definition to auto-configure the node.</DialogDescription>
                </DialogHeader>

                {candidates.length === 0 ? (
                    <div className="flex-1 flex flex-col gap-2 min-h-[300px]">
                        <Textarea
                            placeholder='{"swagger": "2.0", "paths": { ... } }'
                            className="flex-1 font-mono text-xs"
                            value={json}
                            onChange={e => setJson(e.target.value)}
                        />
                        <Button onClick={parseSwagger}>
                            <Upload className="w-4 h-4 mr-2" /> Parse JSON
                        </Button>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto space-y-2 max-h-[400px]">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">Found {candidates.length} Endpoints:</p>
                        {candidates.map((c, i) => (
                            <div key={i} className="flex items-center justify-between p-2 border rounded hover:bg-slate-50 cursor-pointer"
                                onClick={() => {
                                    onImport({
                                        method: c.method,
                                        url: c.fullUrl,
                                        body: generateBodySample(c.body)
                                    });
                                    setOpen(false);
                                    setCandidates([]);
                                }}>
                                <div className="flex items-center gap-2">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white bg-slate-600`}>{c.method}</span>
                                    <span className="text-xs font-mono">{c.path}</span>
                                </div>
                                <span className="text-xs text-muted-foreground truncate max-w-[200px]">{c.summary}</span>
                            </div>
                        ))}
                    </div>
                )}

                <DialogFooter className="sm:justify-start">
                    {candidates.length > 0 && (
                        <Button variant="ghost" onClick={() => setCandidates([])} size="sm">Back to JSON</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
